import { makeStyles } from '@mui/styles';
import { Link } from 'react-admin';
import React, { useEffect, useState } from 'react';
import { Col, message, Modal, notification, Row, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { clsx } from 'clsx';
import { ButtonBase } from '@mui/material';
import dayjs from 'dayjs';

import { Credit, SyntageAuth, SyntageProfile } from '@types';
import { BlueButton, InitialCard, InitialJson, InitialTitle } from '@components';
import { syntageHttp, syntageAuthHttp, userHttp } from '@network';
import { useRequest } from '@hooks';
import { dateFormatter, isProdServer, rbac } from '@utils';

export const CreditIncomeVerification = ({ credit }: {credit: Credit}) => {
  const cls = useStyles();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [syntageProfile, setSyntageProfile] = useState<SyntageProfile | null>(null);
  const [syntageAuth, setSyntageAuth] = useState<SyntageAuth | null>(null);
  const [userUuid, setUserUuid] = useState<string | null>(null);
  const hasSyntageAccess = rbac.hasSyntageAccess();

  const refreshSyntageProfile = async () => {
    const nextProfile = await syntageHttp.getProfileByUserId(credit.userId);
    setSyntageProfile(nextProfile);
  };

  useEffect(() => {
    if (isModalVisible) refreshSyntageProfile();
  }, [isModalVisible]);

  useEffect(() => {
    (async () => {
      const nextSyntageAuth = await syntageAuthHttp.getAuth(credit.userId);
      const nextProfile = await syntageHttp.getProfileByUserId(credit.userId);
      const nextUserUuid = await userHttp.getUuidById(credit.userId);
      setSyntageProfile(nextProfile);
      setSyntageAuth(nextSyntageAuth);
      setUserUuid(nextUserUuid);
    })();
  }, [credit.userId, isModalVisible]);

  const { submit: createProfile, loading: createProfileLoading } = useRequest(async () => {
    if (!hasSyntageAccess) return;
    const nextProfile = await syntageHttp.createProfile(credit.userId);
    setSyntageProfile(nextProfile);
    notification.success({ message: 'Created Successfully', placement: 'bottom' });
  });

  const { submit: rfcChange, loading: rfcChangeLoading } = useRequest(async () => {
    if (!hasSyntageAccess) return;
    const nextProfile = await syntageHttp.rfcChange(credit.userId);
    setSyntageProfile(nextProfile);
    notification.success({ message: 'RFC changed successfully', placement: 'bottom' });
  });

  const { submit: ciecChange, loading: ciecChangeLoading } = useRequest(async () => {
    if (!hasSyntageAccess) return;
    const nextProfile = await syntageHttp.ciecChange(credit.userId);
    setSyntageProfile(nextProfile);
    notification.success({ message: 'CIEC changed successfully', placement: 'bottom' });
  });

  const { submit: runIncome, loading: runIncomeLoading } = useRequest(async () => {
    if (!hasSyntageAccess) return;
    const nextProfile = await syntageHttp.runIncome(credit.userId);
    setSyntageProfile(nextProfile);
    notification.success({ message: 'Income Statements loaded', placement: 'bottom' });
  });

  const { submit: runInvoices, loading: runInvoicesLoading } = useRequest(async () => {
    if (!hasSyntageAccess) return;
    const nextProfile = await syntageHttp.runInvoices(credit.userId);
    setSyntageProfile(nextProfile);
    notification.success({ message: 'List Invoices loaded', placement: 'bottom' });
  });

  const { submit: runClientsData, loading: runClientsDataLoading } = useRequest(async () => {
    if (!hasSyntageAccess) return;
    const nextProfile = await syntageHttp.runClientsData(credit.userId);
    setSyntageProfile(nextProfile);
    notification.success({ message: 'Clients Data loaded', placement: 'bottom' });
  });

  const { submit: runExport, loading: runExportLoading } = useRequest(async () => {
    if (!hasSyntageAccess) return;
    const nextProfile = await syntageHttp.runExport(credit.userId);
    setSyntageProfile(nextProfile);
    notification.success({ message: 'Export Data loaded', placement: 'bottom' });
  });

  const copyLink = async (e: any) => {
    e.stopPropagation();
    const domain = isProdServer() ? 'https://verqor.com' : 'https://dev.verqor.com';
    const syntageAuthLink = `${domain}/verification?id=${userUuid}&partner_name=Verqor`;
    await navigator.clipboard.writeText(syntageAuthLink);
    message.info('The link was copied to clipboard.');
  };

  const showRfcChanged = syntageProfile?.isRfcChanged;
  const showCiecChanged = syntageProfile?.isCiecChanged && !showRfcChanged;
  const hasAnyChanges = showRfcChanged || showCiecChanged;

  const showCreateSatProfile = !syntageProfile && !hasAnyChanges;
  const showGoToSat = syntageProfile?.entityLink && !hasAnyChanges;
  const showTryAgain = syntageProfile?.entityError && !showGoToSat && !hasAnyChanges;

  const showModal = () => void setIsModalVisible(true);
  const hideModal = () => void setIsModalVisible(false);


  return (
    <InitialCard left="Income verification" autoHeight extraPadding>
      <div className={cls.box}>
        {syntageAuth?.isActive ? (
          <div className={cls.auth}>
            <Link to={`/user/${credit.userId}?credit-bureau`} className={cls.btn}>
              <BlueButton className={cls.btn}>
                Go to Credit Bureau
              </BlueButton>
            </Link>
            <div className={cls.authComment}>
              Last login at: {dayjs(syntageAuth.updatedAt).format('DD/MM/YYYY HH:mm')}
            </div>
          </div>
        ) : (
          <div className={cls.auth}>
            {userUuid && (
              <>
                <BlueButton className={cls.btn} onClick={copyLink}>
                  Copy link
                </BlueButton>
                <div className={cls.authComment}>
                  {'Client authorization is missing or expired.'}<br />
                  {'Send the authorization link to the client.'}
                </div>
              </>
            )}
          </div>
        )}

        <div className={cls.buttons}>
          {showCreateSatProfile && (
            <BlueButton
              className={cls.btn}
              onClick={createProfile}
              loading={createProfileLoading}
              disabled={!hasSyntageAccess}
            >Create SAT Profile</BlueButton>
          )}

          {showRfcChanged && (
            <>
              <ButtonBase
                className={clsx(cls.btn, cls.btnIcon)}
                onClick={showModal}
                disabled={!hasSyntageAccess}
              >
                <ErrorIcon /> RFC has been changed
                {syntageProfile?.credentialStatus ? ': ' + syntageProfile.credentialStatus : ''}
              </ButtonBase>
              <BlueButton
                className={cls.btn}
                onClick={rfcChange}
                loading={rfcChangeLoading}
                disabled={!hasSyntageAccess}
              >Update RFC @ SAT Profile</BlueButton>
            </>
          )}

          {showCiecChanged && (
            <>
              <ButtonBase
                className={clsx(cls.btn, cls.btnIcon)}
                onClick={showModal}
                disabled={!hasSyntageAccess}
              >
                <ErrorIcon /> CIEC has been changed
                {syntageProfile?.credentialStatus ? ': ' + syntageProfile.credentialStatus : ''}
              </ButtonBase>
              <BlueButton
                className={cls.btn}
                onClick={ciecChange}
                loading={ciecChangeLoading}
                disabled={!hasSyntageAccess}
              >Update CIEC @ SAT Profile</BlueButton>
            </>
          )}

          {showGoToSat && (
            <>
              <ButtonBase
                className={clsx(cls.btn, cls.btnIcon)}
                onClick={showModal}
                disabled={!hasSyntageAccess}
              >
                <SuccessIcon /> Created Successfully
                {syntageProfile?.credentialStatus ? ': ' + syntageProfile.credentialStatus : ''}
              </ButtonBase>
              <Link to={syntageProfile.entityLink || ''} className={cls.btn} target="_blank">
                <BlueButton
                  className={cls.btn}
                  onClick={createProfile}
                  loading={createProfileLoading}
                  disabled={!hasSyntageAccess}
                >Go to SAT</BlueButton>
              </Link>
            </>
          )}

          {showTryAgain && (
            <>
              <ButtonBase
                className={clsx(cls.btn, cls.btnIcon)}
                onClick={showModal}
                disabled={!hasSyntageAccess}
              >
                <ErrorIcon /> Error Upon Creation
              </ButtonBase>
              <BlueButton
                className={cls.btn}
                onClick={createProfile}
                loading={createProfileLoading}
                disabled={!hasSyntageAccess}
              >Try Again SAT Profile</BlueButton>
            </>
          )}

          {syntageProfile?.syntageAt && (
            <div className={cls.profileUpdate}>
              Last update: {dateFormatter.toDateSlash(syntageProfile.syntageAt, true)}
              &nbsp;| {syntageProfile.adminName || 'N/A'}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={isModalVisible}
        title={false}
        footer={false}
        width={1400}
        onCancel={hideModal}
      >
        <Row gutter={[40, 20]}>
          <Col xs={24} sm={24} md={6}>
            <div className={cls.title}>Syntage API</div>
            <InitialTitle title="Entity ID" fullWidth mt={24}>
              {syntageProfile?.entityId ? (
                <Link to={syntageProfile.entityLink || ''} target="_blank" className={cls.link}>
                  {syntageProfile.entityId}
                </Link>
              ) : <span>N/A</span>}
            </InitialTitle>
            <InitialTitle title="Credential status" fullWidth mt={24}>
              {syntageProfile?.credentialStatus === 'valid' && (
                <span style={{ paddingRight: 10 }}><SuccessIcon /></span>
              )}
              {syntageProfile?.credentialStatus || 'N/A'}
            </InitialTitle>
            {syntageProfile?.entityError ? (
              <InitialJson title="Entity - ERROR"
                json={syntageProfile.entityError} mt={24} isError />
            ) : (
              <InitialJson title="Entity Response"
                json={syntageProfile?.entityResponse} mt={24} />
            )}
            {syntageProfile?.entityError ? (
              <InitialJson title="Credential - ERROR"
                json={syntageProfile?.credentialError} mt={24} isError />
            ) : (
              <InitialJson title="Credential Response"
                json={syntageProfile?.credentialResponse} mt={24} />
            )}
            {syntageProfile?.onboardingId && (
              <InitialTitle title="Onboarding ID" fullWidth mt={20}>
                <Link to={syntageProfile?.onboardingLink ?? ''}
                  target="_blank" className={cls.link}>
                  {syntageProfile?.onboardingId}
                </Link>
              </InitialTitle>
            )}
          </Col>

          <Col xs={24} sm={24} md={18}>
            <div className={cls.title}>Extractions</div>

            {/* Income Statements */}
            <div className={cls.extraction}>
              <div className={cls.extractionHeader}>
                <ButtonBase className={cls.extractionBtn}
                  onClick={runIncome} disabled={runIncomeLoading}>
                  Run Income Statements
                </ButtonBase>
                <ExtractionStatus status={runIncomeLoading ? 'loading'
                  : syntageProfile?.incomeResponse ? 'ready'
                    : syntageProfile?.incomeError ? 'error' : null}
                />
              </div>
              <Row gutter={[20, 20]}>
                <Col xs={24}>
                  {(syntageProfile?.profitData?.rows ?? []).length > 0 && (
                    <Table size="small" pagination={false} className={cls.income}
                      dataSource={syntageProfile?.profitData.rows}
                      columns={syntageProfile?.profitData.columns.map(column => ({
                        title: column.charAt(0).toUpperCase() + column.slice(1),
                        dataIndex: column,
                        key: column,
                      }))}
                    />
                  )}
                  {(syntageProfile?.income?.rows ?? []).length > 0 && (
                    <Table size="small" pagination={false} className={cls.income}
                      dataSource={syntageProfile?.income.rows}
                      columns={syntageProfile?.income.columns.map(column => ({
                        title: column.charAt(0).toUpperCase() + column.slice(1),
                        dataIndex: column,
                        key: column,
                      }))}
                    />
                  )}
                </Col>
                <Col xs={24}>
                  {syntageProfile?.incomeResponse && (
                    <InitialJson title="Income Statements Response"
                      json={syntageProfile.incomeResponse} />
                  )}
                  {syntageProfile?.incomeError && (
                    <InitialJson title="Income Statements ERROR"
                      json={syntageProfile.incomeError} isError />
                  )}
                </Col>
              </Row>
            </div>

            {/* List Invoices */}
            <div className={cls.extraction}>
              <div className={cls.extractionHeader}>
                <ButtonBase className={cls.extractionBtn}
                  onClick={runInvoices} disabled={runInvoicesLoading}>
                  Run List Invoices
                </ButtonBase>
                <ExtractionStatus status={runInvoicesLoading ? 'loading'
                  : syntageProfile?.invoicesResponse ? 'ready'
                    : syntageProfile?.invoicesError ? 'error' : null}
                />
              </div>
              {(syntageProfile?.invoices?.rows ?? []).length > 0 && (
                <Table size="small" scroll={{ x: 'max-content' }} className={cls.income}
                  dataSource={syntageProfile?.invoices.rows}
                  columns={syntageProfile?.invoices.columns.map(column => ({
                    title: column.charAt(0).toUpperCase() + column.slice(1),
                    dataIndex: column,
                    key: column,
                  }))}
                />
              )}
              {syntageProfile?.invoicesError && (
                <InitialJson title="List Invoices ERROR" mt={20}
                  json={syntageProfile.invoicesError} isError />
              )}
            </div>

            {/* Clients Data */}
            <div className={cls.extraction}>
              <div className={cls.extractionHeader}>
                <ButtonBase className={cls.extractionBtn}
                  onClick={runClientsData} disabled={runClientsDataLoading}>
                  Run Clients Data
                </ButtonBase>
                <ExtractionStatus status={runClientsDataLoading ? 'loading'
                  : syntageProfile?.concentration ? 'ready'
                    : syntageProfile?.concentrationError ? 'error' : null}
                />
              </div>
              {(syntageProfile?.clientsData?.rows ?? []).length > 0 && (
                <Table size="small" scroll={{ x: 'max-content' }} className={cls.income}
                  dataSource={syntageProfile?.clientsData.rows}
                  columns={syntageProfile?.clientsData.columns.map(column => ({
                    title: column.charAt(0).toUpperCase() + column.slice(1),
                    dataIndex: column,
                    key: column,
                  }))}
                  pagination={false}
                />
              )}
              {syntageProfile?.concentrationResponse && (
                <InitialJson title="Customer Concentration Response" mt={20}
                  json={syntageProfile.concentrationResponse} />
              )}
              {syntageProfile?.concentrationError && (
                <InitialJson title="Customer Concentration ERROR" mt={20}
                  json={syntageProfile.concentrationError} isError />
              )}
              {syntageProfile?.networkResponse && (
                <InitialJson title="Customer Network Response" mt={20}
                  json={syntageProfile.networkResponse} />
              )}
              {syntageProfile?.networkError && (
                <InitialJson title="Customer Network ERROR" mt={20}
                  json={syntageProfile.networkError} isError />
              )}
              {syntageProfile?.invoicesResponse && (
                <InitialJson title="Customer Invoices Response" mt={20}
                  json={syntageProfile.invoicesResponse} />
              )}
              {syntageProfile?.invoicesError && (
                <InitialJson title="Customer Invoices ERROR" mt={20}
                  json={syntageProfile.invoicesError} isError />
              )}
            </div>

            {/* Export Data */}
            <div className={cls.extraction}>
              <div className={cls.extractionHeader}>
                <div>
                  <ButtonBase className={cls.extractionBtn}
                    onClick={runExport} disabled={runExportLoading}>
                    Run Export Data
                  </ButtonBase>
                  <ExtractionStatus status={runExportLoading ? 'loading'
                    : syntageProfile?.exportResponse ? 'ready'
                      : syntageProfile?.exportError ? 'error' : null}
                  />
                </div>
                <div>
                  {syntageProfile?.exportLink && (
                    <Link to={syntageProfile?.exportLink ?? ''} target="_blank">
                      <ButtonBase className={cls.extractionBtn}>
                        Open Export Documents
                      </ButtonBase>
                    </Link>
                  )}
                </div>
              </div>
              {syntageProfile?.exportResponse && (
                <InitialJson title="Export Data Response" mt={20}
                  json={syntageProfile.exportResponse} />
              )}
              {syntageProfile?.exportError && (
                <InitialJson title="Export Data ERROR" mt={20}
                  json={syntageProfile.exportError} isError />
              )}
            </div>
          </Col>
        </Row>

      </Modal>
    </InitialCard>
  );
};

const ExtractionStatus = ({ status }: {status: 'loading' | 'ready' | 'error' | null}) => {
  const cls = useStyles();

  if (status === 'loading') {
    return (
      <div className={clsx(cls.label, cls.labelLoading)}>
        <LoadingOutlined spin className={cls.labelSpin} />
        <span className={cls.labelText}>Loading</span>
      </div>
    );
  }

  if (status === 'ready') {
    return (
      <div className={clsx(cls.label, cls.labelReady)}>
        <svg width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M0.31968 2.0439C0.72451 1.66823 1.35723 1.69187 1.73291 2.0967L4.45378 5.02873L9.58485 0.267209C9.98968 -0.108464 10.6224 -0.0848287 10.9981 0.320001C11.3738 0.724832 11.3501 1.35756 10.9453 1.73323L5.08121 7.17497C4.70174 7.52711 4.12185 7.52864 3.74131 7.19402C3.71587 7.17165 3.69152 7.14755 3.66798 7.12218L0.266888 3.45713C-0.108786 3.0523 -0.0851503 2.41958 0.31968 2.0439Z"
            fill="white" />
        </svg>
        <span className={cls.labelText}>Ready</span>
      </div>
    );
  }

  return <></>;
};

const useStyles = makeStyles({
  box: {
    marginTop: 24,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  link: {
    fontSize: 13,
    marginLeft: -20,
    marginRight: -20,
  },
  buttons: {
    position: 'relative',
    maxWidth: 240,
  },
  btn: {
    minWidth: 240,
    '& button': {
      backgroundColor: 'var(--primary-color)',
      color: 'var(--primary-button-color)',
    },
  },
  btnIcon: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: -30,
    height: 30,
    '& svg': {
      marginRight: 10,
    },
  },
  profileUpdate: {
    marginTop: 10,
    fontSize: 11,
    color: 'var(--text-color)',
    marginLeft: -100,
    textAlign: 'right',
  },
  extraction: {
    marginTop: 20,
    paddingBottom: 15,
  },
  extractionHeader: {
    display: 'flex',
    alignItems: 'center',
    '& > div': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  extractionBtn: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
    borderRadius: 8,
    height: '36px !important',
    lineHeight: '36px',
    paddingLeft: 12,
    paddingRight: 12,
    fontSize: 16,
    color: 'var(--primary-color)',
    '&:hover': {
      borderColor: 'var(--primary-color) !important',
    },
  },
  label: {
    margin: '0 15px',
    height: '26px',
    lineHeight: '26px',
    paddingLeft: 10,
    paddingRight: 10,
    borderRadius: 10,
    fontSize: 18,
    fontWeight: 'bold',
  },
  labelLoading: {
    backgroundColor: '#D9D9D9',
    color: '#000',
  },
  labelReady: {
    backgroundColor: '#0C5541',
    color: '#FFF',
  },
  labelSpin: {
    fontSize: 14,
    color: 'var(--primary-color)',
  },
  labelText: {
    marginLeft: 6,
  },
  income: {
    marginTop: 8,
    '& td': {
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
    },
  },
  auth: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
  },
  authComment: {
    fontSize: 12,
    color: 'var(--text-color)',
    marginTop: 4,
    marginBottom: -16,
  },
});

const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect width="16" height="16" rx="8" fill="#275D3C" />
    <path d="M7.42773 9.57129L10.9992 5.99981"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round" />
    <path d="M7.42969 9.57129L6.0011 8.1427" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect width="16" height="16" rx="8" fill="#D32F2F" />
    <rect x="3.75781"
      y="5.17188"
      width="2"
      height="10"
      rx="1"
      transform="rotate(-45 3.75781 5.17188)"
      fill="white" />
    <rect x="10.8281"
      y="3.75781"
      width="2"
      height="10"
      rx="1"
      transform="rotate(45 10.8281 3.75781)"
      fill="white" />
  </svg>
);
